/**
 * Group Evidence tab — statutory document recommendations and linked evidence.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import {
  ArrowBendUpRightIcon,
  ArrowCounterClockwiseIcon,
  BuildingIcon,
  CaretDownIcon,
  CaretUpIcon,
  ClockIcon,
  DotsThreeVerticalIcon,
  FileTextIcon,
  FunnelIcon,
  GraduationCapIcon,
  InfoIcon,
  LinkSimpleIcon,
  MagnifyingGlassIcon,
  NotePencilIcon,
  PencilSimpleIcon,
  PlusIcon,
  ProhibitIcon,
  TrashIcon,
  UploadSimpleIcon,
  UserIcon,
  WarningIcon,
  WarningCircleIcon
} from '@phosphor-icons/react';
import { toast } from '@/components/ui/toast';

import Banner from '@/components/ui/Banner';
import Button from 'src/components/ui/Button';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import InputField from '@/components/ui/Input';
import Dropdown from '@/components/ui/Dropdown';
import Loading from 'src/components/ui/Loading';
import Modal from '@/components/ui/Modal';
import Textarea from '@/components/ui/Textarea';
import TanStackTable from '@/components/ui/TanStackTable';
import CompliancePill from '@/components/ui/CompliancePill';
import Pill from '@/components/ui/Pill';
import { Menu, MenuItem } from '@/components/ui/Menu';
import Popover from '@/components/ui/Popover';
import Tooltip from '@/components/ui/Tooltip';
import { DonutChart } from '@/components/charts';
import DocumentPickerModal from 'src/components/DocumentPickerModal';
import OtherEvidenceModal, { type OtherEvidencePayload } from '@/components/OtherEvidenceModal';
import ImportPamModal from './ImportPamModal';
import PamQuestionPill from 'src/features/pam/components/PamQuestionPill';
import { groupHubResourcePath, openResourceLabel, resourcePath } from 'src/features/chat/components/ResourceLink';
import { ResourcePopover } from 'src/features/chat/components/ResourcePopover';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import DocumentSourcePills from 'src/features/groups/components/DocumentSourcePills';
import {
  daysUntilStatutoryExpiry,
  formatStatutoryExpiryDate,
  hasStatutoryFileOrNoteEvidence,
  statutoryExpiryDisplay
} from 'src/features/groups/components/StatutoryComplianceSummary';
import {
  statutoryComplianceSegments,
  statutoryCountTotal,
  statutoryExpiringWindowLabel
} from 'src/features/groups/components/statutoryChartSpecs';
import * as hubApi from 'src/features/document-hub/api';
import BatchUploadModal from 'src/features/uploads/components/BatchUploadModal';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { getEfmDomainLabel } from 'src/features/domains/efmDomains';
import {
  addGroupStatutoryNote,
  deleteGroupStatutoryNote,
  dismissGroupStatutoryDocument,
  getGroupStatutoryDocuments,
  linkGroupStatutoryDocument,
  restoreGroupStatutoryDocument,
  unlinkGroupStatutoryDocument,
  updateGroupStatutoryNote,
  type StatutoryDocumentRow
} from 'src/features/groups/api';
import { columnVisibilityForWidth } from './columnVisibility';

const GROUP_FOLDER_PREFIX = 'groups/';
type AddExistingPicker = 'link' | 'move' | null;
type EvidenceStatusFilter =
  | 'all'
  | 'needs_action'
  | 'present'
  | 'expiring'
  | 'pending'
  | 'expired'
  | 'missing'
  | 'dismissed';
type EvidenceRequirementFilter = 'all' | 'required' | 'optional';

const STATUS_PARAM = 'status';
const REQUIREMENT_PARAM = 'requirement';
const SEARCH_PARAM = 'q';
const VISUALISATIONS_STORAGE_KEY = 'evidence-visualisations-visible';
const DISCLAIMER_DISMISSED_KEY = 'group-evidence-intro-dismissed';
const NISTA_DISCLAIMER_DISMISSED_KEY = 'group-evidence-nista-intro-dismissed';
const STATUS_FILTERS = [
  'all',
  'needs_action',
  'present',
  'expiring',
  'pending',
  'expired',
  'missing',
  'dismissed'
] as const;
const DISMISSAL_REASON_CHIPS = ['Not applicable', 'Derogation', 'Covered at trust level'] as const;
const REQUIREMENT_FILTERS = ['all', 'required', 'optional'] as const;

function readVisualisationsVisible(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(VISUALISATIONS_STORAGE_KEY) !== 'false';
}

function readDisclaimerDismissed(key: string): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(key) === 'true';
}

function isStatusFilter(value: string | null): value is EvidenceStatusFilter {
  return value !== null && (STATUS_FILTERS as readonly string[]).includes(value);
}

function isRequirementFilter(value: string | null): value is EvidenceRequirementFilter {
  return value !== null && (REQUIREMENT_FILTERS as readonly string[]).includes(value);
}

function compareDismissedLast(a: StatutoryDocumentRow, b: StatutoryDocumentRow): number {
  const aDismissed = a.dismissal != null;
  const bDismissed = b.dismissal != null;
  if (aDismissed === bDismissed) return 0;
  return aDismissed ? 1 : -1;
}

function evidenceRowStatus(
  document: StatutoryDocumentRow
): 'present' | 'expiring' | 'pending' | 'expired' | 'missing' | 'optional' | 'dismissed' {
  if (document.dismissal != null) return 'dismissed';
  if (!document.required) return 'optional';
  if (!hasStatutoryFileOrNoteEvidence(document)) return 'missing';
  if (document.expiry_status === 'expired') return 'expired';
  if (document.expiry_status === 'pending') return 'pending';
  if (document.expiry_status === 'expiring') return 'expiring';
  return 'present';
}

/** Hide first when the statutory table is tight (sidebar + narrow pane). */
const STATUTORY_HIDE_ORDER = ['expiryDate'] as const;
const STATUTORY_COLUMN_MIN_WIDTH: Record<string, number> = {
  name: 200,
  linkedDocuments: 260,
  expiryDate: 140,
  required: 176,
  requirementSource: 220,
  rowActions: 48
};
const STATUTORY_CELL_GUTTER = 24;

export function groupEvidenceTabPath(groupId: string): string {
  return `/groups/${groupId}/evidence`;
}

interface Props {
  groupId: string;
  groupName: string;
  canWrite: boolean;
  /** Safety groups can pull evidence from a PAM submission. PFI groups cannot. */
  showPamActions?: boolean;
}

function StatutoryExpiryCell({ document }: { document: StatutoryDocumentRow }) {
  const display = statutoryExpiryDisplay(document);
  const isExpired = document.expiry_status === 'expired';
  const isPending = document.expiry_status === 'pending';
  const isExpiring = document.expiry_status === 'expiring';
  const fileCount = document.linked_documents.filter((linked) => linked.expiry_date).length;
  const title =
    display.kind === 'date'
      ? fileCount > 1
        ? 'Earliest expiry among attached files'
        : 'Expiry of the attached file'
      : display.kind === 'no_expiry'
        ? 'This requirement does not have a renewal cycle'
        : 'No expiry date is recorded on the attached evidence';

  return (
    <Tooltip title={title} arrow>
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap ${
          isExpired
            ? 'text-red-600'
            : isPending || isExpiring || display.kind === 'not_set'
              ? 'text-amber-700'
              : 'text-content-subtle'
        }`}
      >
        {isExpired && (
          <WarningCircleIcon size={16} weight="fill" className="shrink-0 text-misc-error" aria-label="Expired" />
        )}
        {isPending && (
          <ClockIcon size={16} weight="bold" className="shrink-0 text-amber-500" aria-label="Not started yet" />
        )}
        {isExpiring && (
          <WarningIcon
            size={16}
            weight="fill"
            className="shrink-0 text-amber-500"
            aria-label={`Expires ${statutoryExpiringWindowLabel(document)}`}
          />
        )}
        {display.label}
      </span>
    </Tooltip>
  );
}

function statutoryWriteChatQuery(document: StatutoryDocumentRow, groupName: string): string {
  const intro = `Please help me write a ${document.name} for ${groupName}.`;
  const description = document.description.trim();
  if (!description) return intro;
  return `${intro}\n\n${description}`;
}

function StatutoryDocumentAddMenu({
  document,
  groupId,
  groupName,
  onUpload,
  onLink,
  onMove,
  onNote,
  disabled = false
}: {
  document: StatutoryDocumentRow;
  groupId: string;
  groupName: string;
  onUpload: (document: StatutoryDocumentRow) => void;
  onLink: (document: StatutoryDocumentRow) => void;
  onMove: (document: StatutoryDocumentRow) => void;
  onNote: (document: StatutoryDocumentRow) => void;
  disabled?: boolean;
}) {
  const navigate = useTenantNavigate();
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <Button
        ref={addButtonRef}
        variant="ghost"
        size="sm"
        icon={PlusIcon}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        disabledReason="You need to be a Contributor or Admin to add evidence in this group."
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        Add
      </Button>
      <Menu open={open} anchorEl={addButtonRef.current} onClose={close} placement="bottom-start" className="w-80">
        <MenuItem
          icon={UploadSimpleIcon}
          description="Upload new files into this group."
          onClick={() => {
            close();
            onUpload(document);
          }}
        >
          Upload files
        </MenuItem>
        <MenuItem
          icon={LinkSimpleIcon}
          description="Link from this group or the Organisation Folder."
          onClick={() => {
            close();
            onLink(document);
          }}
        >
          Link File
        </MenuItem>
        <MenuItem
          icon={ArrowBendUpRightIcon}
          description="This group becomes the owner of the file."
          onClick={() => {
            close();
            onMove(document);
          }}
        >
          Move from Document Hub
        </MenuItem>
        <MenuItem
          icon={NotePencilIcon}
          description="Add a note, contact, or external link — same as PAM other evidence."
          onClick={() => {
            close();
            onNote(document);
          }}
        >
          Note or link
        </MenuItem>
        <MenuItem
          icon={PencilSimpleIcon}
          description="Write this missing file using CompliMind."
          onClick={() => {
            close();
            const params = new URLSearchParams({
              q: statutoryWriteChatQuery(document, groupName),
              resource_id: groupId,
              resource_type: 'team_group',
              resource_title: groupName
            });
            navigate(`/chat?${params.toString()}`);
          }}
        >
          Write
        </MenuItem>
      </Menu>
    </>
  );
}

function StatutoryDocumentRowMenu({
  document,
  canWrite,
  onDismiss,
  onRestore
}: {
  document: StatutoryDocumentRow;
  canWrite: boolean;
  onDismiss: (document: StatutoryDocumentRow) => void;
  onRestore: (document: StatutoryDocumentRow) => void;
}) {
  const dismissed = document.dismissal != null;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return (
    <div
      className="flex justify-end"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <Button
        variant="ghost"
        size="sm"
        icon={DotsThreeVerticalIcon}
        iconProps={{ weight: 'regular', className: 'text-content-default' }}
        aria-label="Requirement actions"
        aria-haspopup="menu"
        aria-expanded={anchorEl != null}
        disabled={!canWrite}
        disabledReason="You need to be a Contributor or Admin to dismiss requirements in this group."
        onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
      />
      <Menu open={anchorEl != null} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} placement="bottom-end">
        <MenuItem
          icon={ProhibitIcon}
          onClick={() => {
            setAnchorEl(null);
            if (dismissed) onRestore(document);
            else onDismiss(document);
          }}
        >
          {dismissed ? 'Restore requirement' : 'Dismiss requirement'}
        </MenuItem>
      </Menu>
    </div>
  );
}

type StatutoryNote = StatutoryDocumentRow['other_evidence'][number];

function displayLinkHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '') || url;
  } catch {
    return url;
  }
}

function externalHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function statutoryNoteLabel(note: StatutoryNote): string {
  if (note.text !== null && note.text !== '') return note.text;
  if (note.external_link !== null && note.external_link !== '') return displayLinkHost(note.external_link);
  if (note.contact_name !== null && note.contact_name !== '') return note.contact_name;
  return 'Evidence';
}

function statutoryNoteSublabel(note: StatutoryNote): string | null {
  const parts: string[] = [];
  if (note.text !== null && note.text !== '' && note.external_link !== null && note.external_link !== '') {
    parts.push(displayLinkHost(note.external_link));
  }
  if (note.contact_name !== null && note.contact_name !== '') {
    const details = note.contact_details !== null && note.contact_details !== '' ? ` (${note.contact_details})` : '';
    parts.push(`${note.contact_name}${details}`);
  } else if (note.contact_details !== null && note.contact_details !== '') {
    parts.push(note.contact_details);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function StatutoryEvidenceItemRow({
  glyph: Glyph,
  label,
  sublabel,
  sublabelClassName = 'text-content-subtle',
  canWrite,
  isRemoving,
  href,
  onActivate,
  activateLabel,
  onEdit,
  onRemove,
  popover
}: {
  glyph: typeof FileTextIcon;
  label: string;
  sublabel?: string | null;
  sublabelClassName?: string;
  canWrite: boolean;
  isRemoving: boolean;
  href?: string;
  onActivate?: () => void;
  activateLabel?: string;
  onEdit?: () => void;
  onRemove?: () => void;
  popover?: { resourceId: string; resourceType: string; href: string };
}) {
  const location = useLocation();
  const tenantCode = resolveTenantCode(location.pathname);
  const popoverHref = popover ? (tenantCode ? toTenantPath(popover.href, tenantCode) : popover.href) : null;
  const showEdit = canWrite && onEdit != null;
  const showRemove = canWrite && onRemove != null;
  const actionCount = (showEdit ? 1 : 0) + (showRemove ? 1 : 0);

  const content = (
    <>
      <Glyph size={18} aria-hidden className="shrink-0 text-content-subtle" />
      <span className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
        <span className="truncate text-sm text-content-default" title={label}>
          {label}
        </span>
        {sublabel && (
          <span className={`truncate text-xs ${sublabelClassName}`} title={sublabel}>
            {sublabel}
          </span>
        )}
      </span>
    </>
  );

  const row = (
    <div className="group relative flex w-fit min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-lg border border-border-subtle bg-surface-hover px-3 py-1 transition-colors hover:bg-bluegray-100">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-w-0 items-center gap-2 text-left"
          aria-label={activateLabel ?? `Open ${label}`}
        >
          {content}
        </a>
      ) : (
        <button
          type="button"
          className="flex min-w-0 items-center gap-2 text-left disabled:cursor-default"
          onClick={onActivate}
          disabled={!onActivate}
          aria-label={activateLabel ?? label}
        >
          {content}
        </button>
      )}
      {actionCount > 0 && (
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center rounded-r-lg bg-surface-hover pl-1 pr-0.5 opacity-0 group-hover:pointer-events-auto group-hover:bg-bluegray-100 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
          {showEdit && (
            <Button
              variant="ghost"
              size="sm"
              icon={PencilSimpleIcon}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEdit();
              }}
              aria-label={`Edit evidence ${label}`}
            />
          )}
          {showRemove && (
            <Button
              variant="ghost"
              size="sm"
              icon={TrashIcon}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onRemove();
              }}
              disabled={isRemoving}
              aria-label={`Remove evidence ${label}`}
            />
          )}
        </span>
      )}
    </div>
  );

  if (!popover || !popoverHref) return row;

  return (
    <ResourcePopover
      resourceId={popover.resourceId}
      href={popoverHref}
      openLabel={openResourceLabel(popover.resourceType)}
    >
      {row}
    </ResourcePopover>
  );
}

function StatutoryOtherEvidenceRow({
  note,
  canWrite,
  isRemoving,
  onEdit,
  onRemove
}: {
  note: StatutoryNote;
  canWrite: boolean;
  isRemoving: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const label = statutoryNoteLabel(note);
  const link = note.external_link;
  const hasLink = link !== null && link !== '';
  return (
    <StatutoryEvidenceItemRow
      glyph={hasLink ? LinkSimpleIcon : note.contact_name !== null ? UserIcon : FileTextIcon}
      label={label}
      sublabel={statutoryNoteSublabel(note)}
      canWrite={canWrite}
      isRemoving={isRemoving}
      href={hasLink ? externalHref(link) : undefined}
      onActivate={hasLink || !canWrite ? undefined : onEdit}
      activateLabel={hasLink ? `Open ${displayLinkHost(link)}` : canWrite ? `Edit evidence ${label}` : label}
      onEdit={canWrite ? onEdit : undefined}
      onRemove={onRemove}
    />
  );
}

function StatutoryLinkedDocumentRow({
  name,
  documentId,
  canWrite,
  isRemoving,
  onRemove
}: {
  name: string;
  documentId: string;
  canWrite: boolean;
  isRemoving: boolean;
  onRemove: () => void;
}) {
  const navigate = useTenantNavigate();
  return (
    <StatutoryEvidenceItemRow
      glyph={FileTextIcon}
      label={name}
      canWrite={canWrite}
      isRemoving={isRemoving}
      onActivate={() => navigate(resourcePath('document', documentId))}
      activateLabel={`Open ${name}`}
      onRemove={onRemove}
      popover={{
        resourceId: documentId,
        resourceType: 'document',
        href: resourcePath('document', documentId)
      }}
    />
  );
}

function StatutoryTrainingEvidenceRow({
  item,
  groupId
}: {
  item: StatutoryDocumentRow['training_evidence'][number];
  groupId: string;
}) {
  const navigate = useTenantNavigate();
  const resourceType = 'training_appointment';
  const href = groupHubResourcePath(groupId, resourceType, item.id) ?? `/groups/${groupId}/training`;
  const pending = (daysUntilStatutoryExpiry(item.starts_on) ?? 0) > 0;
  return (
    <StatutoryEvidenceItemRow
      glyph={GraduationCapIcon}
      label={item.label}
      sublabel={
        pending ? `From Training Hub · starts ${formatStatutoryExpiryDate(item.starts_on)}` : 'From Training Hub'
      }
      sublabelClassName={pending ? 'text-amber-700' : undefined}
      canWrite={false}
      isRemoving={false}
      onActivate={() => navigate(href)}
      activateLabel={`Open ${item.label} in Training`}
      popover={{ resourceId: item.id, resourceType, href }}
    />
  );
}

export default function GroupEvidenceTab({ groupId, groupName, canWrite, showPamActions = true }: Props) {
  const disclaimerDismissedKey = showPamActions ? DISCLAIMER_DISMISSED_KEY : NISTA_DISCLAIMER_DISMISSED_KEY;
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingRecommendationRef = useRef<StatutoryDocumentRow | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [dismissalReason, setDismissalReason] = useState('');
  const [viewingDismissal, setViewingDismissal] = useState<StatutoryDocumentRow | null>(null);
  const [editingNote, setEditingNote] = useState<StatutoryDocumentRow['other_evidence'][number] | null>(null);
  const [addExistingPicker, setAddExistingPicker] = useState<AddExistingPicker>(null);
  const [importPamOpen, setImportPamOpen] = useState(false);
  const [showVisualisations, setShowVisualisations] = useState(readVisualisationsVisible);
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(() => readDisclaimerDismissed(disclaimerDismissedKey));

  useEffect(() => {
    setDisclaimerDismissed(readDisclaimerDismissed(disclaimerDismissedKey));
  }, [disclaimerDismissedKey]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLElement | null>(null);
  const [pendingEvidenceRemoval, setPendingEvidenceRemoval] = useState<{
    message: string;
    run: () => void;
  } | null>(null);
  const queryClient = useQueryClient();
  const navigate = useTenantNavigate();
  const statutoryTableRef = useRef<HTMLDivElement>(null);
  const [statutoryTableWidth, setStatutoryTableWidth] = useState(0);

  const filterStatus: EvidenceStatusFilter = isStatusFilter(searchParams.get(STATUS_PARAM))
    ? (searchParams.get(STATUS_PARAM) as EvidenceStatusFilter)
    : 'all';
  const filterRequirement: EvidenceRequirementFilter = isRequirementFilter(searchParams.get(REQUIREMENT_PARAM))
    ? (searchParams.get(REQUIREMENT_PARAM) as EvidenceRequirementFilter)
    : 'all';
  const searchQuery = searchParams.get(SEARCH_PARAM) ?? '';

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setFilterStatus = useCallback(
    (value: EvidenceStatusFilter) => {
      updateSearchParams({ [STATUS_PARAM]: value === 'all' ? null : value });
    },
    [updateSearchParams]
  );

  const setFilterRequirement = useCallback(
    (value: EvidenceRequirementFilter) => {
      updateSearchParams({ [REQUIREMENT_PARAM]: value === 'all' ? null : value });
    },
    [updateSearchParams]
  );

  const setSearchQuery = useCallback(
    (value: string) => {
      updateSearchParams({ [SEARCH_PARAM]: value.trim() ? value : null });
    },
    [updateSearchParams]
  );

  const hasCustomFilters = filterStatus !== 'all' || filterRequirement !== 'all' || searchQuery.trim() !== '';

  const resetFilters = useCallback(() => {
    updateSearchParams({
      [STATUS_PARAM]: null,
      [REQUIREMENT_PARAM]: null,
      [SEARCH_PARAM]: null
    });
  }, [updateSearchParams]);

  const dismissDisclaimer = useCallback(() => {
    window.localStorage.setItem(disclaimerDismissedKey, 'true');
    setDisclaimerDismissed(true);
  }, [disclaimerDismissedKey]);

  const toggleVisualisations = useCallback(() => {
    setShowVisualisations((current) => {
      const next = !current;
      window.localStorage.setItem(VISUALISATIONS_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const { data: statutoryDocuments, isLoading: statutoryLoading } = useQuery({
    queryKey: ['group-statutory-documents', groupId],
    queryFn: () => getGroupStatutoryDocuments(groupId)
  });

  useEffect(() => {
    const node = statutoryTableRef.current;
    if (!node) return;
    const update = () => setStatutoryTableWidth(node.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [statutoryLoading]);

  const invalidateHubAndStatutory = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['group-statutory-documents', groupId] });
    void queryClient.invalidateQueries({ queryKey: ['document-hub'] });
    void queryClient.invalidateQueries({ queryKey: ['groups', 'directory-snapshot'] });
  }, [groupId, queryClient]);

  const linkStatutoryMutation = useMutation({
    mutationFn: ({ recommendationId, documentIds }: { recommendationId: string; documentIds: string[] }) =>
      linkGroupStatutoryDocument(groupId, recommendationId, { document_ids: documentIds }),
    onSuccess: () => {
      invalidateHubAndStatutory();
      toast.success('Document linked');
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? 'Failed to link document')
  });

  const attachDocuments = useCallback(
    (documentIds: string[]) => {
      const recommendationId = pendingRecommendationRef.current?.recommendation_id;
      if (!recommendationId || documentIds.length === 0) return;
      linkStatutoryMutation.mutate({ recommendationId, documentIds });
      pendingRecommendationRef.current = null;
    },
    [linkStatutoryMutation]
  );

  const unlinkMutation = useMutation({
    mutationFn: ({ recommendationId, documentId }: { recommendationId: string; documentId: string }) =>
      unlinkGroupStatutoryDocument(groupId, recommendationId, documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-statutory-documents', groupId] });
    }
  });

  const {
    data: groupFolderFileIds,
    isSuccess: groupFilesSuccess,
    isFetching: groupFilesFetching,
    isError: groupFilesError,
    refetch: refetchGroupFiles
  } = useQuery({
    queryKey: ['document-hub', 'group-file-ids', groupId],
    queryFn: async () => {
      const groupFolderId = `${GROUP_FOLDER_PREFIX}${groupId}`;
      const linked: string[] = [];
      const owned: string[] = [];
      let offset = 0;
      const limit = 200;
      while (true) {
        const page = await hubApi.getFolderContents(groupFolderId, { files_only: true, offset, limit });
        for (const node of page.items) {
          if (!node.document_id) continue;
          linked.push(node.document_id);
          if (node.group_relation === 'owned') owned.push(node.document_id);
        }
        offset += page.items.length;
        if (offset >= page.total || page.items.length === 0) break;
      }
      return { linked, owned };
    },
    enabled: addExistingPicker !== null,
    refetchOnMount: 'always'
  });

  const groupFilesReady = groupFilesSuccess && !groupFilesFetching && !groupFilesError;
  const alreadyOwnedDocumentIds = groupFolderFileIds?.owned ?? [];
  const alreadyOnRecommendationIds = useMemo(() => {
    const recommendationId = pendingRecommendationRef.current?.recommendation_id;
    if (!recommendationId) return [];
    return (
      statutoryDocuments?.documents
        .find((row) => row.recommendation_id === recommendationId)
        ?.linked_documents.map((linked) => linked.id) ?? []
    );
  }, [addExistingPicker, statutoryDocuments]);

  const linkFilesMutation = useMutation({
    mutationFn: async (documentIds: string[]) => {
      const alreadyInGroup = new Set(groupFolderFileIds?.linked ?? []);
      const toBringIn = documentIds.filter((id) => !alreadyInGroup.has(id));
      if (toBringIn.length > 0) {
        await hubApi.linkOrganisationLibraryDocuments(groupId, toBringIn);
      }
      return documentIds;
    },
    onSuccess: (documentIds) => {
      setAddExistingPicker(null);
      attachDocuments(documentIds);
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? 'Failed to link files')
  });

  const moveToGroupMutation = useMutation({
    mutationFn: (documentIds: string[]) => hubApi.moveDocumentsToGroup(groupId, documentIds),
    onSuccess: (_data, documentIds) => {
      setAddExistingPicker(null);
      attachDocuments(documentIds);
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      const looksLikeId = typeof detail === 'string' && !/\s/.test(detail.trim());
      toast.error(typeof detail === 'string' && detail.trim() && !looksLikeId ? detail : 'Failed to move files');
    }
  });

  const openStatutoryUpload = useCallback((document: StatutoryDocumentRow) => {
    pendingRecommendationRef.current = document;
    setUploadModalOpen(true);
  }, []);

  const openStatutoryLink = useCallback((document: StatutoryDocumentRow) => {
    pendingRecommendationRef.current = document;
    setAddExistingPicker('link');
  }, []);

  const openStatutoryMove = useCallback((document: StatutoryDocumentRow) => {
    pendingRecommendationRef.current = document;
    setAddExistingPicker('move');
  }, []);

  const openStatutoryNote = useCallback((document: StatutoryDocumentRow) => {
    pendingRecommendationRef.current = document;
    setEditingNote(null);
    setNoteModalOpen(true);
  }, []);

  const openStatutoryDismiss = useCallback((document: StatutoryDocumentRow) => {
    pendingRecommendationRef.current = document;
    setDismissalReason(document.dismissal?.reason ?? '');
    setDismissModalOpen(true);
  }, []);

  const dismissMutation = useMutation({
    mutationFn: ({ recommendationId, reason }: { recommendationId: string; reason: string | null }) =>
      dismissGroupStatutoryDocument(groupId, recommendationId, reason),
    onSuccess: () => {
      setDismissModalOpen(false);
      setDismissalReason('');
      pendingRecommendationRef.current = null;
      invalidateHubAndStatutory();
      toast.success('Requirement dismissed');
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? 'Failed to dismiss requirement')
  });

  const restoreMutation = useMutation({
    mutationFn: (recommendationId: string) => restoreGroupStatutoryDocument(groupId, recommendationId),
    onSuccess: () => {
      setViewingDismissal(null);
      invalidateHubAndStatutory();
      toast.success('Requirement restored');
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? 'Failed to restore requirement')
  });

  const restoreStatutory = useCallback(
    (document: StatutoryDocumentRow) => {
      restoreMutation.mutate(document.recommendation_id);
    },
    [restoreMutation]
  );

  const openEditStatutoryNote = useCallback(
    (document: StatutoryDocumentRow, note: StatutoryDocumentRow['other_evidence'][number]) => {
      pendingRecommendationRef.current = document;
      setEditingNote(note);
      setNoteModalOpen(true);
    },
    []
  );

  const saveNoteMutation = useMutation({
    mutationFn: (payload: OtherEvidencePayload) => {
      const recommendationId = pendingRecommendationRef.current?.recommendation_id;
      if (!recommendationId) throw new Error('Missing recommendation');
      if (editingNote) {
        return updateGroupStatutoryNote(groupId, recommendationId, editingNote.id, payload);
      }
      return addGroupStatutoryNote(groupId, recommendationId, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-statutory-documents', groupId] });
    }
  });

  const deleteNoteMutation = useMutation({
    mutationFn: ({ recommendationId, noteId }: { recommendationId: string; noteId: string }) =>
      deleteGroupStatutoryNote(groupId, recommendationId, noteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-statutory-documents', groupId] });
    }
  });

  const statutoryColumns = useMemo<ColumnDef<StatutoryDocumentRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Requirement',
        size: 240,
        minSize: 200,
        maxSize: 320,
        enableResizing: false,
        enableSorting: false,
        cell: ({ row }) => {
          const document = row.original;
          const dismissed = document.dismissal != null;
          return (
            <span
              className={`min-w-0 break-words font-medium ${
                dismissed ? 'text-content-subtlest' : 'text-content-default'
              }`}
            >
              {document.name}
              {document.description && (
                <>
                  {' '}
                  <Tooltip title={document.description} arrow>
                    <button
                      type="button"
                      aria-label={`About ${document.name}`}
                      className="inline-flex align-text-bottom text-content-subtle hover:text-content-default"
                    >
                      <InfoIcon size={14} />
                    </button>
                  </Tooltip>
                </>
              )}
            </span>
          );
        }
      },
      {
        id: 'linkedDocuments',
        header: 'Evidence',
        size: 320,
        minSize: 260,
        enableResizing: false,
        enableSorting: false,
        cell: ({ row }) => {
          const document = row.original;
          const evidenceRows = [
            ...(document.training_evidence ?? []).map((item) => (
              <StatutoryTrainingEvidenceRow key={item.id} item={item} groupId={groupId} />
            )),
            ...(document.other_evidence ?? []).map((note) => (
              <StatutoryOtherEvidenceRow
                key={note.id}
                note={note}
                canWrite={canWrite}
                isRemoving={deleteNoteMutation.isPending}
                onEdit={() => openEditStatutoryNote(document, note)}
                onRemove={() =>
                  setPendingEvidenceRemoval({
                    message: 'This permanently removes this note or link from the requirement.',
                    run: () =>
                      deleteNoteMutation.mutate({
                        recommendationId: document.recommendation_id,
                        noteId: note.id
                      })
                  })
                }
              />
            )),
            ...document.linked_documents.map((linked) => (
              <StatutoryLinkedDocumentRow
                key={linked.id}
                name={linked.name}
                documentId={linked.id}
                canWrite={canWrite}
                isRemoving={unlinkMutation.isPending}
                onRemove={() =>
                  setPendingEvidenceRemoval({
                    message: 'This unlinks the file from this requirement. The file itself is not deleted.',
                    run: () =>
                      unlinkMutation.mutate({
                        recommendationId: document.recommendation_id,
                        documentId: linked.id
                      })
                  })
                }
              />
            ))
          ];
          const leadingRows = evidenceRows.slice(0, -1);
          const lastRow = evidenceRows.at(-1);
          if (document.dismissal != null) {
            const actor = document.dismissal.created_by_name || 'Unknown';
            return (
              <p className="min-w-0 text-xs text-content-subtle flex items-center gap-2">
                Dismissed by {actor}{' '}
                <button
                  type="button"
                  className="font-medium text-misc-link underline underline-offset-2"
                  onClick={(event) => {
                    event.stopPropagation();
                    setViewingDismissal(document);
                  }}
                >
                  View
                </button>
              </p>
            );
          }
          return (
            <div className="flex min-w-0 w-full flex-col items-start gap-1">
              {leadingRows}
              <div className="flex min-w-0 w-full items-center gap-2">
                {lastRow ? <div className="min-w-0 overflow-hidden">{lastRow}</div> : null}
                <span className="shrink-0">
                  <StatutoryDocumentAddMenu
                    document={document}
                    groupId={groupId}
                    groupName={groupName}
                    onUpload={openStatutoryUpload}
                    onLink={openStatutoryLink}
                    onMove={openStatutoryMove}
                    onNote={openStatutoryNote}
                    disabled={!canWrite}
                  />
                </span>
              </div>
            </div>
          );
        }
      },
      {
        id: 'expiryDate',
        accessorKey: 'expiry_date',
        header: 'Evidence expiry',
        size: 148,
        minSize: 140,
        enableResizing: false,
        enableSorting: true,
        sortUndefined: 'last',
        sortingFn: (rowA, rowB) => {
          const dismissedOrder = compareDismissedLast(rowA.original, rowB.original);
          if (dismissedOrder !== 0) return dismissedOrder;
          const a = rowA.original.expiry_date;
          const b = rowB.original.expiry_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        cell: ({ row }) => <StatutoryExpiryCell document={row.original} />
      },
      {
        id: 'required',
        accessorKey: 'required',
        header: 'Status',
        size: 184,
        minSize: 176,
        enableResizing: false,
        enableSorting: false,
        cell: ({ row }) => {
          const document = row.original;
          if (document.dismissal != null) {
            return (
              <CompliancePill
                text="Dismissed"
                stop="na"
                tooltip={document.dismissal.reason || 'This requirement does not apply to this group'}
              />
            );
          }
          if (!document.required) {
            return (
              <Pill size="sm" className="bg-surface-hover text-content-subtle">
                Optional
              </Pill>
            );
          }
          if (!hasStatutoryFileOrNoteEvidence(document)) {
            return <CompliancePill text="Missing" stop={1} tooltip="Required document has no linked evidence" />;
          }
          if (document.expiry_status === 'expired') {
            return <CompliancePill text="Expired" stop={1} tooltip="Required document evidence has expired" />;
          }
          if (document.expiry_status === 'pending') {
            return <CompliancePill text="Pending" stop={2} tooltip="Required document evidence has not started yet" />;
          }
          if (document.expiry_status === 'expiring') {
            return (
              <CompliancePill
                text={`Expiring ${statutoryExpiringWindowLabel(document)}`}
                stop={2}
                tooltip={`Required document evidence expires ${statutoryExpiringWindowLabel(document)}`}
              />
            );
          }
          return <CompliancePill text="Evidenced" stop={5} tooltip="Required document has attached evidence" />;
        }
      },
      {
        id: 'requirementSource',
        header: 'Requirement Source',
        minSize: 220,
        size: 260,
        enableResizing: false,
        enableSorting: false,
        cell: ({ row }) => {
          const document = row.original;
          return (
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1.5 overflow-hidden">
              {document.pam_questions.map((question) => (
                <PamQuestionPill key={question.id} question={question} />
              ))}
              <DocumentSourcePills sources={document.sources} />
            </div>
          );
        }
      },
      {
        id: 'rowActions',
        header: '',
        size: 48,
        minSize: 48,
        maxSize: 48,
        enableResizing: false,
        enableSorting: false,
        cell: ({ row }) => (
          <StatutoryDocumentRowMenu
            document={row.original}
            canWrite={canWrite}
            onDismiss={openStatutoryDismiss}
            onRestore={restoreStatutory}
          />
        )
      }
    ],
    [
      canWrite,
      deleteNoteMutation,
      groupId,
      groupName,
      navigate,
      openEditStatutoryNote,
      openStatutoryLink,
      openStatutoryMove,
      openStatutoryDismiss,
      setViewingDismissal,
      openStatutoryNote,
      openStatutoryUpload,
      restoreStatutory,
      unlinkMutation
    ]
  );

  const statutoryColumnVisibility = useMemo(() => {
    const columnIds = statutoryColumns.map((column) => column.id).filter((id): id is string => !!id);
    return columnVisibilityForWidth(statutoryTableWidth, columnIds, {
      hideOrder: STATUTORY_HIDE_ORDER,
      minWidths: STATUTORY_COLUMN_MIN_WIDTH,
      gutter: STATUTORY_CELL_GUTTER
    });
  }, [statutoryColumns, statutoryTableWidth]);

  const allDocuments = statutoryDocuments?.documents ?? [];

  const filteredDocuments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matches = allDocuments.filter((document) => {
      if (filterRequirement === 'required' && !document.required) return false;
      if (filterRequirement === 'optional' && document.required) return false;

      const status = evidenceRowStatus(document);
      if (filterStatus === 'needs_action') {
        if (status !== 'missing' && status !== 'expired' && status !== 'expiring') return false;
      } else if (filterStatus !== 'all' && status !== filterStatus) {
        return false;
      }

      if (!query) return true;
      const linkedNames = document.linked_documents.map((linked) => linked.name).join(' ');
      const sourceNames = document.sources.map((source) => source.document_name).join(' ');
      const haystack = `${document.name} ${document.description} ${linkedNames} ${sourceNames}`.toLowerCase();
      return haystack.includes(query);
    });
    return [...matches].sort(compareDismissedLast);
  }, [allDocuments, filterRequirement, filterStatus, searchQuery]);

  const complianceCounts = useMemo(() => {
    let present = 0;
    let expiring = 0;
    let pending = 0;
    let expired = 0;
    let missing = 0;
    for (const document of filteredDocuments) {
      if (!document.required) continue;
      const status = evidenceRowStatus(document);
      if (status === 'missing') missing += 1;
      else if (status === 'expired') expired += 1;
      else if (status === 'pending') pending += 1;
      else if (status === 'expiring') expiring += 1;
      else if (status === 'present') present += 1;
    }
    return { present, expiring, pending, expired, missing };
  }, [filteredDocuments]);

  const complianceSegments = useMemo(() => statutoryComplianceSegments(complianceCounts), [complianceCounts]);
  const requiredCount = statutoryCountTotal(complianceCounts);
  const currentlyPresent = complianceCounts.present + complianceCounts.expiring;
  const completePercent = requiredCount === 0 ? 0 : Math.round((currentlyPresent / requiredCount) * 100);

  const statusFilterOptions = useMemo(
    () => [
      { label: 'All statuses', value: 'all' },
      { label: 'Needs action', value: 'needs_action' },
      { label: 'Evidenced', value: 'present' },
      { label: 'Expiring', value: 'expiring' },
      { label: 'Pending', value: 'pending' },
      { label: 'Expired', value: 'expired' },
      { label: 'Missing', value: 'missing' },
      { label: 'Dismissed', value: 'dismissed' }
    ],
    []
  );

  const requirementFilterOptions = useMemo(
    () => [
      { label: 'All documents', value: 'all' },
      { label: 'Required', value: 'required' },
      { label: 'Optional', value: 'optional' }
    ],
    []
  );

  return (
    <div className="flex-1 flex flex-col gap-2 md:rounded-lg min-w-0">
      {!disclaimerDismissed && (
        <Banner variant="info" icon={InfoIcon} onDismiss={dismissDisclaimer}>
          {showPamActions ? (
            <>
              CompliMind recommends the documents below as core evidence for this group. Link files here to track their
              status year-round and prepare for audits and PAM submissions.{' '}
            </>
          ) : (
            <>
              These documents follow NISTA&apos;s Contract Document Checklist: the contract documents you should
              typically hold. Some may not apply to this contract, and yours may require others. Link the files you hold
              and dismiss anything that doesn&apos;t apply.{' '}
            </>
          )}
          <a
            href={`mailto:support@complimind.co.uk?subject=${encodeURIComponent(
              showPamActions
                ? `Feedback on statutory documents for ${groupName}`
                : `Feedback on the contract document checklist for ${groupName}`
            )}`}
            className="font-medium underline underline-offset-2 hover:text-blue-900"
          >
            Give us feedback
          </a>
        </Banner>
      )}
      <div className="@container flex flex-col gap-2">
        <div className="flex w-full min-w-0 items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="search-bar min-w-0 w-64 max-w-full">
              <InputField
                icon={MagnifyingGlassIcon}
                placeholder="Search evidence..."
                state="default"
                type="search"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="hidden @[900px]:flex items-center gap-2 shrink-0">
              <Dropdown
                options={requirementFilterOptions}
                initialValue={filterRequirement}
                onChange={(value) => setFilterRequirement(value as EvidenceRequirementFilter)}
                width="w-[160px]"
                placeholder="All documents"
              />
              <Dropdown
                options={statusFilterOptions}
                initialValue={filterStatus}
                onChange={(value) => setFilterStatus(value as EvidenceStatusFilter)}
                width="w-[160px]"
                placeholder="All statuses"
              />
            </div>
            <span ref={setFilterButtonEl} className="shrink-0 @[900px]:hidden">
              <Button
                type="button"
                variant={hasCustomFilters ? 'primary' : 'outline'}
                size="md"
                icon={FunnelIcon}
                aria-label="Filters"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen((open) => !open)}
              />
            </span>
            <Popover
              open={filtersOpen}
              anchorEl={filterButtonEl}
              onClose={() => setFiltersOpen(false)}
              placement="bottom-end"
              className="w-[220px] p-3"
            >
              <div className="flex flex-col gap-2">
                <Dropdown
                  options={requirementFilterOptions}
                  initialValue={filterRequirement}
                  onChange={(value) => setFilterRequirement(value as EvidenceRequirementFilter)}
                  width="w-full"
                  placeholder="All documents"
                />
                <Dropdown
                  options={statusFilterOptions}
                  initialValue={filterStatus}
                  onChange={(value) => setFilterStatus(value as EvidenceStatusFilter)}
                  width="w-full"
                  placeholder="All statuses"
                />
              </div>
            </Popover>
          </div>
          {showPamActions && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="md"
                icon={BuildingIcon}
                text="Import from PAM"
                disabled={!canWrite}
                onClick={() => setImportPamOpen(true)}
              />
              <Tooltip
                title={`You will be able to use evidence listed here to auto fill your ${
                  statutoryDocuments?.domain ? getEfmDomainLabel(statutoryDocuments.domain) : 'domain'
                } submission SAQs in the 2027 PAM submission`}
                arrow
              >
                <span>
                  <Button type="button" variant="outline" size="md" icon={BuildingIcon} text="Autofill PAM" disabled />
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-xs text-content-subtle flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span>
              {filteredDocuments.length} {filteredDocuments.length === 1 ? 'document matches' : 'documents match'} your
              filters
            </span>
            {hasCustomFilters && (
              <>
                <span>·</span>
                <button type="button" onClick={resetFilters} className="underline text-misc-link">
                  Reset filters
                </button>
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon={showVisualisations ? CaretUpIcon : CaretDownIcon}
            iconPosition="right"
            text={showVisualisations ? 'Hide charts' : 'Show charts'}
            onClick={toggleVisualisations}
            className="shrink-0 text-content-default"
          />
        </div>

        {showVisualisations && (
          <DonutChart
            className="w-fit"
            title="Requirements evidenced"
            segments={complianceSegments}
            ariaLabel="Required documents by compliance status"
            centerValue={`${completePercent}%`}
            centerLabel="complete"
            emptyMessage="No required documents in this selection"
          />
        )}

        {statutoryLoading ? (
          <Loading text="Loading recommended documents…" />
        ) : (
          <div ref={statutoryTableRef} className="w-full min-w-0">
            <TanStackTable
              data={filteredDocuments}
              columns={statutoryColumns}
              getRowId={(row) => row.recommendation_id}
              enableSorting
              enableResizing={false}
              autoRowHeight
              cellVerticalAlign="middle"
              headerSize="comfortable"
              columnVisibility={statutoryColumnVisibility}
              itemsPerPage={100}
              emptyMessage={
                allDocuments.length === 0
                  ? "No recommended documents are set up for this group's domain yet."
                  : 'No documents match your filters.'
              }
            />
          </div>
        )}
      </div>

      <ConfirmModal
        open={pendingEvidenceRemoval != null}
        onClose={() => setPendingEvidenceRemoval(null)}
        onConfirm={() => {
          pendingEvidenceRemoval?.run();
          setPendingEvidenceRemoval(null);
        }}
        title="Remove evidence?"
        message={pendingEvidenceRemoval?.message ?? ''}
        confirmText="Remove"
        variant="danger"
        isLoading={unlinkMutation.isPending || deleteNoteMutation.isPending}
        disableBackdropClick
        modal="trap-focus"
      />
      <Modal
        open={viewingDismissal?.dismissal != null}
        onClose={() => setViewingDismissal(null)}
        title={viewingDismissal?.name ?? 'Dismissal'}
        className="w-full max-w-lg"
        cta={
          <Button
            variant="primary"
            icon={ArrowCounterClockwiseIcon}
            disabled={!canWrite || restoreMutation.isPending}
            disabledReason="You need to be a Contributor or Admin to restore requirements in this group."
            onClick={() => {
              if (viewingDismissal == null) return;
              restoreStatutory(viewingDismissal);
            }}
          >
            Restore
          </Button>
        }
      >
        {viewingDismissal?.dismissal != null && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-content-subtle">{viewingDismissal.dismissal.reason || 'No reason provided'}</p>
            <p className="text-xs text-content-subtlest">
              Dismissed by {viewingDismissal.dismissal.created_by_name || 'Unknown'} on{' '}
              {new Date(viewingDismissal.dismissal.created_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </Modal>
      <Modal
        open={dismissModalOpen}
        onClose={() => {
          setDismissModalOpen(false);
          setDismissalReason('');
          pendingRecommendationRef.current = null;
        }}
        title="Dismiss requirement"
        className="w-full max-w-lg"
        cta={
          <Button
            variant="primary"
            onClick={() => {
              const recommendationId = pendingRecommendationRef.current?.recommendation_id;
              if (!recommendationId) return;
              const trimmed = dismissalReason.trim();
              dismissMutation.mutate({
                recommendationId,
                reason: trimmed === '' ? null : trimmed
              });
            }}
            disabled={dismissMutation.isPending}
          >
            Dismiss
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-content-subtle">
            This requirement will no longer count toward this group&apos;s statutory compliance. You can restore it
            later.
          </p>
          <Textarea
            value={dismissalReason}
            onChange={(event) => setDismissalReason(event.target.value)}
            placeholder="Optional reason (for example, not applicable to this trust)"
            aria-label="Dismissal reason"
            rows={1}
            fullWidth
          />
          <div className="flex flex-wrap gap-2">
            {DISMISSAL_REASON_CHIPS.map((option) => (
              <Button
                key={option}
                variant="outline"
                size="sm"
                aria-label={`Select dismissal reason: ${option}`}
                onClick={() => setDismissalReason(option)}
              >
                {option}
              </Button>
            ))}
          </div>
        </div>
      </Modal>
      <OtherEvidenceModal
        open={noteModalOpen}
        onClose={() => {
          setNoteModalOpen(false);
          setEditingNote(null);
          pendingRecommendationRef.current = null;
        }}
        mode={
          editingNote
            ? {
                kind: 'edit',
                initialText: editingNote.text,
                initialContactName: editingNote.contact_name,
                initialContactDetails: editingNote.contact_details,
                initialExternalLink: editingNote.external_link
              }
            : { kind: 'create' }
        }
        onSave={async (payload) => {
          await saveNoteMutation.mutateAsync(payload);
        }}
      />
      {showPamActions && (
        <ImportPamModal
          open={importPamOpen}
          onClose={() => setImportPamOpen(false)}
          groupId={groupId}
          groupName={groupName}
          documents={statutoryDocuments?.documents ?? []}
          domain={statutoryDocuments?.domain ?? null}
        />
      )}
      <BatchUploadModal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
        }}
        onComplete={(result) => {
          setUploadModalOpen(false);
          attachDocuments(result.files.map((file) => file.fileId));
          invalidateHubAndStatutory();
        }}
        title={`Upload to ${groupName}`}
        groupId={groupId}
        completingMessage="Uploading…"
        allowFolder
        backgroundOnUpload
      />
      <DocumentPickerModal
        open={addExistingPicker === 'link'}
        onClose={() => {
          setAddExistingPicker(null);
          pendingRecommendationRef.current = null;
        }}
        title="Link File"
        confirmLabel="Link"
        isLoading={linkFilesMutation.isPending}
        contentsReady={groupFilesReady}
        contentsError={groupFilesError ? 'Could not load files already in this group.' : null}
        onRetryContents={() => {
          void refetchGroupFiles();
        }}
        excludeDocumentIds={alreadyOnRecommendationIds}
        filesOnly={false}
        omitDrafts
        visibleRootFolderIds={[`${GROUP_FOLDER_PREFIX}${groupId}`, 'organisation']}
        onConfirm={(documentIds) => linkFilesMutation.mutate(documentIds)}
      />
      <DocumentPickerModal
        open={addExistingPicker === 'move'}
        onClose={() => {
          setAddExistingPicker(null);
          pendingRecommendationRef.current = null;
        }}
        title={groupName.trim() ? `Move from Document Hub to ${groupName.trim()}` : 'Move from Document Hub'}
        confirmLabel="Move"
        isLoading={moveToGroupMutation.isPending}
        contentsReady={groupFilesReady}
        contentsError={groupFilesError ? 'Could not load files already in this group.' : null}
        onRetryContents={() => {
          void refetchGroupFiles();
        }}
        disabledDocumentIds={[...alreadyOwnedDocumentIds, ...alreadyOnRecommendationIds]}
        disabledDocumentReason={groupName.trim() ? `Already in ${groupName.trim()}` : 'Already in this group'}
        filesOnly={false}
        omitDrafts
        moveAccessWarning={{ groupName }}
        onConfirm={(documentIds) => moveToGroupMutation.mutate(documentIds)}
      />
    </div>
  );
}
